import { useState } from 'react';
import LeafletMap from '../../components/feature/LeafletMap';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'adresse e-mail est requise';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Veuillez entrer une adresse e-mail valide';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Le numéro de téléphone est requis';
    }

    if (!formData.subject) {
      newErrors.subject = 'Veuillez sélectionner un sujet';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.length > 500) {
      newErrors.message = 'Le message ne doit pas dépasser 500 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6jvgpipaf6ievb71m0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
        setErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a5bb44d32e2cb583aa9b2c385851d6bf.jpg"
            alt="Contact Us"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-bold mb-4">Contactez-nous</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Discutons ensemble de la façon dont nous pouvons transformer votre activité
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 lg:py-28 bg-brand-dark">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Nos coordonnées
              </h2>
              <p className="text-lg text-gray-400 mb-12 leading-relaxed">
                Notre équipe est disponible pour répondre à vos questions et discuter de vos besoins. Contactez-nous via l'un des canaux suivants.
              </p>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-teal/10 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-brand-teal"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Nos bureaux</h3>
                    <p className="text-gray-400">
                      Paris, France<br />
                      Lyon, France<br />
                      Les berges du Lac 1, Tunisie<br />
                      ITEC SYSTEMS AND SOFTWARE CONSULTING – FZCO, Dubaï
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-teal/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-brand-teal"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">E-mail</h3>
                    <a href="mailto:contact@itecgroupe.com" className="text-gray-400 hover:text-brand-teal transition-colors cursor-pointer">
                      contact@itecgroupe.com
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-teal/10 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-brand-teal"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Téléphone</h3>
                    <a href="tel:+33603053264" className="text-gray-400 hover:text-brand-teal transition-colors cursor-pointer">
                      +33 6 03 05 32 64
                    </a>
                  </div>
                </div>

                {/* LinkedIn */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-teal/10 rounded-lg flex-shrink-0">
                    <i className="ri-linkedin-fill text-2xl text-brand-teal"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">LinkedIn</h3>
                    <a
                      href="https://linkedin.com/company/itec1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-brand-teal transition-colors cursor-pointer"
                    >
                      Suivez-nous sur LinkedIn
                    </a>
                  </div>
                </div>
              </div>

              {/* Leaflet Map */}
              <div className="mt-12 rounded-lg overflow-hidden shadow-lg border border-white/10">
                <div className="bg-brand-dark-2 px-5 py-3 flex flex-wrap gap-4 border-b border-white/10">
                  {[
                    { flag: '🇫🇷', city: 'Paris' },
                    { flag: '🇫🇷', city: 'Lyon' },
                    { flag: '🇹🇳', city: 'Tunis' },
                    { flag: '🇦🇪', city: 'Dubai' },
                  ].map((o) => (
                    <span key={o.city} className="flex items-center gap-1.5 text-sm text-gray-300">
                      <span>{o.flag}</span>
                      <span>{o.city}</span>
                    </span>
                  ))}
                </div>
                <LeafletMap className="h-[320px] md:h-[380px]" />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-brand-dark-card rounded-2xl p-8 lg:p-10 border border-white/10">
                <h2 className="text-3xl font-bold text-white mb-6">Envoyez-nous un message</h2>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-green-400 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-green-300 font-medium">Message envoyé avec succès !</p>
                      <p className="text-green-400 text-sm mt-1">Nous vous répondrons dans les plus brefs délais.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
                    <i className="ri-error-warning-fill text-red-400 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-red-300 font-medium">Une erreur est survenue</p>
                      <p className="text-red-400 text-sm mt-1">Veuillez réessayer plus tard ou nous contacter directement.</p>
                    </div>
                  </div>
                )}

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Nom complet <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 text-sm border ${errors.name ? 'border-red-500' : 'border-white/10'} bg-brand-dark rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all`}
                      placeholder="Jean Dupont"
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Adresse e-mail <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 text-sm border ${errors.email ? 'border-red-500' : 'border-white/10'} bg-brand-dark rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all`}
                      placeholder="jean.dupont@entreprise.com"
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      Numéro de téléphone <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 text-sm border ${errors.phone ? 'border-red-500' : 'border-white/10'} bg-brand-dark rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all`}
                      placeholder="+33 1 23 45 67 89"
                    />
                    {errors.phone && <p className="mt-1 text-sm text-red-400">{errors.phone}</p>}
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                      Nom de l'entreprise
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-sm border border-white/10 bg-brand-dark rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all"
                      placeholder="Votre entreprise"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                      Sujet <span className="text-red-400">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 text-sm border ${errors.subject ? 'border-red-500' : 'border-white/10'} bg-brand-dark rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all cursor-pointer`}
                    >
                      <option value="" className="bg-brand-dark">Sélectionnez un sujet</option>
                      <option value="Conseil IT" className="bg-brand-dark">Conseil IT</option>
                      <option value="Services d'ingénierie" className="bg-brand-dark">Services d'ingénierie</option>
                      <option value="Transformation digitale" className="bg-brand-dark">Transformation digitale</option>
                      <option value="Solutions SAP" className="bg-brand-dark">Solutions SAP</option>
                      <option value="Services Cloud" className="bg-brand-dark">Services Cloud</option>
                      <option value="Data & BI" className="bg-brand-dark">Data & BI</option>
                      <option value="Développement mobile" className="bg-brand-dark">Développement mobile</option>
                      <option value="Renseignement général" className="bg-brand-dark">Renseignement général</option>
                      <option value="Partenariat" className="bg-brand-dark">Partenariat</option>
                      <option value="Opportunités de carrière" className="bg-brand-dark">Opportunités de carrière</option>
                    </select>
                    {errors.subject && <p className="mt-1 text-sm text-red-400">{errors.subject}</p>}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      maxLength={500}
                      className={`w-full px-4 py-3 text-sm border ${errors.message ? 'border-red-500' : 'border-white/10'} bg-brand-dark rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all resize-none`}
                      placeholder="Décrivez votre projet ou votre demande..."
                    ></textarea>
                    <div className="flex justify-between items-center mt-1">
                      {errors.message ? (
                        <p className="text-sm text-red-400">{errors.message}</p>
                      ) : (
                        <p className="text-sm text-gray-500">{formData.message.length}/500 caractères</p>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-brand-teal text-white px-8 py-4 rounded-lg font-medium hover:bg-brand-teal-dark transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Envoyer le message
                        <i className="ri-send-plane-fill"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}